import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Sparkles, Headphones } from "lucide-react";
import { API } from "@/lib/api";
import { useT, useLang } from "@/context/LangContext";

function getSessionId(key) {
  let v = localStorage.getItem(key);
  if (!v) {
    v = `${key}-${crypto.randomUUID()}`;
    localStorage.setItem(key, v);
  }
  return v;
}

export default function ChatWidget() {
  const t = useT();
  const { lang } = useLang();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("ai");
  const [aiMsgs, setAiMsgs] = useState([{ role: "assistant", content: t("chat.hello") }]);
  const [supportMsgs, setSupportMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);
  const aiSession = useRef(getSessionId("nego_ai_session"));
  const supSession = useRef(getSessionId("nego_support_session"));

  // Refresh greeting when language changes
  useEffect(() => {
    setAiMsgs((prev) => {
      if (prev.length === 1 && prev[0].role === "assistant") return [{ role: "assistant", content: t("chat.hello") }];
      return prev;
    });
  }, [lang, t]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [aiMsgs, supportMsgs, tab]);

  useEffect(() => {
    if (tab !== "support" || !open) return;
    let stop = false;
    const load = async () => {
      try {
        const r = await fetch(`${API}/support/messages/${supSession.current}`);
        if (r.ok && !stop) setSupportMsgs(await r.json());
      } catch {}
    };
    load();
    const i = setInterval(load, 3500);
    return () => { stop = true; clearInterval(i); };
  }, [tab, open]);

  const sendAi = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setAiMsgs((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const r = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: aiSession.current, message: text, lang }),
      });
      if (!r.ok || !r.body) throw new Error("AI error");
      const reader = r.body.getReader();
      const dec = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = dec.decode(value, { stream: true });
        setAiMsgs((m) => {
          const copy = m.slice();
          copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + chunk };
          return copy;
        });
      }
    } catch (e) {
      setAiMsgs((m) => {
        const copy = m.slice();
        copy[copy.length - 1] = { role: "assistant", content: t("chat.error") };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  const sendSupport = async () => {
    const text = input.trim();
    if (!text) return;
    setInput("");
    try {
      const r = await fetch(`${API}/support/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ session_id: supSession.current, message: text }),
      });
      if (r.ok) {
        const m = await r.json();
        setSupportMsgs((cur) => [...cur, m]);
      }
    } catch {}
  };

  return (
    <>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="chat-toggle"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-[var(--accent)] text-white shadow-[0_10px_30px_rgba(255,59,48,0.45)] hover:scale-105 transition-transform flex items-center justify-center"
        aria-label="Chat"
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </button>

      {open && (
        <div className="fixed bottom-24 right-6 z-50 w-[360px] max-w-[92vw] h-[520px] bg-white border border-black/10 rounded-2xl shadow-2xl flex flex-col overflow-hidden" data-testid="chat-panel">
          <div className="px-4 py-3 bg-[#0A0F1A] text-white flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-widest text-white/50">{t("chat.header")}</p>
              <p className="font-display text-lg">{tab === "ai" ? t("chat.title.ai") : t("chat.title.support")}</p>
            </div>
          </div>

          <div className="flex border-b border-black/10">
            <button onClick={() => setTab("ai")} data-testid="chat-tab-ai" className={`flex-1 py-2 text-xs font-bold tracking-widest uppercase flex items-center justify-center gap-1 ${tab === "ai" ? "text-[var(--accent)] border-b-2 border-[var(--accent)]" : "text-black/50"}`}>
              <Sparkles size={13} /> {t("chat.tab.ai")}
            </button>
            <button onClick={() => setTab("support")} data-testid="chat-tab-support" className={`flex-1 py-2 text-xs font-bold tracking-widest uppercase flex items-center justify-center gap-1 ${tab === "support" ? "text-[var(--accent)] border-b-2 border-[var(--accent)]" : "text-black/50"}`}>
              <Headphones size={13} /> {t("chat.tab.support")}
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 flex flex-col gap-2 bg-white">
            {tab === "ai"
              ? aiMsgs.map((m, i) => (
                  <div key={i} className={m.role === "user" ? "chat-bubble-user" : "chat-bubble-bot"}>
                    {m.content || (streaming && i === aiMsgs.length - 1 ? "…" : "")}
                  </div>
                ))
              : supportMsgs.length === 0 ? (
                <p className="text-xs text-black/50 text-center mt-8">{t("chat.support.empty")}</p>
              ) : supportMsgs.map((m) => (
                  <div key={m.id} className={m.from_role === "customer" ? "chat-bubble-user" : "chat-bubble-bot"}>
                    {m.message}
                  </div>
                ))}
          </div>

          <div className="border-t border-black/10 p-3 flex items-center gap-2 bg-white">
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") (tab === "ai" ? sendAi() : sendSupport()); }}
              placeholder={tab === "ai" ? t("chat.placeholder.ai") : t("chat.placeholder.support")}
              className="input-field !py-2 text-sm"
            />
            <button data-testid="chat-send" onClick={() => (tab === "ai" ? sendAi() : sendSupport())} className="btn-primary !py-2 !px-3" disabled={streaming}>
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
